import { Controller } from '@hotwired/stimulus';

/* stimulusFetch: 'lazy' */
export default class extends Controller {
    static values = {
        country: String,
    };

    static targets = [
        'citySearch',
        'barreauSearch',
        'latitude',
        'longitude',
        'postalCode',
        'city',
        'billingAddressSearch',
        'billingLatitude',
        'billingLongitude',
        'billingPostalCode',
        'billingCity',
    ];

    autocomplete = null;
    autocompleteBillingAddress = null;
    validCityValue = false;
    cityIsValid = false;
    validSpecialismValue = false;
    validBarreauValue = false;
    validPhoneValue = false;

    connect() {
        this.initAutocomplete();
    }

    /*
     * DDT-4046: load the Google Maps Places script here rather than from the global
     * layout. It used to be a <script> tag in layout.html.twig, so all 386 KB of it
     * was fetched on every page of the app — including the login page, which has no
     * address field at all. Only the templates that render this controller need it.
     *
     * Resolves once Maps is ready. Several instances can share one script tag: the
     * first injects it, the rest just wait for the same callback event.
     */
    loadScript() {
        return new Promise((resolve) => {
            if (this.constructor.isGoogleMapsReady()) {
                resolve();

                return;
            }

            window.addEventListener('google-maps-callback', () => resolve(), { once: true });

            if (document.querySelector('script[data-google-maps-places]')) {
                return;
            }

            window.initAutocomplete = function() {
                const event = new Event('google-maps-callback', {
                    bubbles: true,
                    cancelable: true,
                });

                window.dispatchEvent(event);
            };

            const key = document.querySelector('meta[name="google-maps-api-key"]')?.content ?? '';
            const script = document.createElement('script');

            script.dataset.googleMapsPlaces = '';
            script.async = true;
            script.defer = true;
            script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places&callback=initAutocomplete`;

            document.head.appendChild(script);
        });
    }

    static isGoogleMapsReady() {
        return 'undefined' !== typeof google && !!google.maps && !!google.maps.places;
    }

    async initAutocomplete() {
        await this.loadScript();

        // The barreau lookup only applies to forms that render #barreau-container
        // (onboarding/recruitment). The job-offer form on "Mes annonces" also uses
        // this controller, has no barreau field and no such route in `routes`, so
        // fetch(undefined) used to fire a POST to the relative URL "/0/undefined".
        if (
            null !== document.querySelector('#barreau-container')
            && (
                (this.cityTarget.value !== null && this.cityTarget.value !== '')
                || ($('.selected-bar-from-form') && $('.selected-bar-from-form').length > 0)
            )
        ) {
            this.fillBarreau(this.cityTarget.value);
        }

        let setAutocompleteInterval = setInterval(() => {
            // DDT-4046: `!google` threw a ReferenceError on every tick until the script
            // landed, because an undeclared global cannot be read in a module. Harmless
            // before (the throw just aborted that tick) but noisy, and now that the
            // script is injected here the wait is real rather than incidental.
            if (!this.constructor.isGoogleMapsReady()) {
                return;
            }

            clearInterval(setAutocompleteInterval);
            this.autocomplete = new google.maps.places.Autocomplete(this.citySearchTarget, { types: ['address'] });

            this.autocomplete.setFields(['address_component', 'geometry']);

            let componentRestrictions = { country: this.countryValue };

            if ('fr' === this.countryValue) {
                componentRestrictions = { country: ['fr', 're', 'mq', 'gp', 'gf'] };
            }

            this.autocomplete.setComponentRestrictions(componentRestrictions);

            this.autocomplete.addListener('place_changed', this.fillInAddress.bind(this));

            if (this.hasBillingAddressSearchTarget) {
                this.autocompleteBillingAddress = new google.maps.places.Autocomplete(this.billingAddressSearchTarget, { types: ['address'] });

                this.autocompleteBillingAddress.setFields(['address_component', 'geometry']);

                this.autocompleteBillingAddress.setComponentRestrictions(componentRestrictions);

                this.autocompleteBillingAddress.addListener('place_changed', this.fillInBillingAddress.bind(this));
            }

            if (document.querySelector('#lawyer_address_and_specialism_citySearch')) {
                this.autocomplete.addListener('place_changed', () => {
                    document.querySelector('#error-message-address').classList.add('d-none');
                    this.validCityValue = true;
                    this.isAddressAndSpecialismFormValidToBeSubmitted();
                });

                document.querySelector('#lawyer_address_and_specialism_citySearch').addEventListener('blur', () => {
                    setTimeout(() => {
                        const activeElement = document.activeElement;

                        if (activeElement && activeElement.classList.contains('pac-item')) {
                            return;
                        }

                        if (!this.validCityValue) {
                            document.querySelector('#error-message-address').classList.remove('d-none');
                            this.validCityValue = false;
                            this.isAddressAndSpecialismFormValidToBeSubmitted();
                        }
                    }, 200);
                });

                document.querySelector('#lawyer_address_and_specialism_citySearch').addEventListener('input', () => {
                    this.validCityValue = false;
                    this.isAddressAndSpecialismFormValidToBeSubmitted();
                });

                document.querySelector('#lawyer_address_and_specialism_specialisms').addEventListener('change', () => {
                    setTimeout(() => {
                        this.isSpecialismValid();
                        this.isAddressAndSpecialismFormValidToBeSubmitted();
                    }, 0);
                });
            }

            if (document.querySelector('#lawyer_address_and_specialism_barreau')) {
                document.querySelector('#lawyer_address_and_specialism_barreau').addEventListener('change', () => {
                    this.isAddressAndSpecialismBarreauValid();
                    this.isSpecialismValid();
                    this.isAddressAndSpecialismFormValidToBeSubmitted();
                });
            } else if (document.querySelector('#essential_lawyer_self_onboarding_contact_details_bar')) {
                document.querySelector('#essential_lawyer_self_onboarding_contact_details_bar').addEventListener('change', () => {
                    this.isEssentialBarreauValid();
                    this.isEssentialPhoneValid();
                    this.isEssentialFormValidToBeSubmitted();
                });
            } else if (document.querySelector('#free_trial_lawyer_self_onboarding_contact_details_bar')) {
                document.querySelector('#free_trial_lawyer_self_onboarding_contact_details_bar').addEventListener('change', () => {
                    this.isFreeTrialBarreauValid();
                    this.isFreeTrialPhoneValid();
                    this.isFreeTrialFormValidToBeSubmitted();
                });
            } else if (document.querySelector('#freemium_lawyer_self_onboarding_contact_details_bar')) {
                document.querySelector('#freemium_lawyer_self_onboarding_contact_details_bar').addEventListener('change', () => {
                    this.isFreemiumBarreauValid();
                    this.isFreemiumPhoneValid();
                    this.isFreemiumFormValidToBeSubmitted();
                });
            }

            if (document.querySelector('#essential_lawyer_self_onboarding_contact_details_citySearch')) {
                this.autocomplete.addListener('place_changed', () => {
                    document.querySelector('#error-message-address').classList.add('d-none');
                    this.validCityValue = true;

                    this.isEssentialPhoneValid();
                    this.isEssentialFormValidToBeSubmitted();
                });

                document.querySelector('#essential_lawyer_self_onboarding_contact_details_citySearch').addEventListener('blur', (event) => {
                    setTimeout(() => {
                        const activeElement = document.activeElement;

                        if (activeElement && activeElement.classList.contains('pac-item')) {
                            return;
                        }

                        if (!this.validCityValue) {
                            document.querySelector('#error-message-address').classList.remove('d-none');
                            this.validCityValue = false;
                            this.isEssentialFormValidToBeSubmitted();
                        }
                    }, 200);
                });

                document.querySelector('#essential_lawyer_self_onboarding_contact_details_citySearch').addEventListener('input', () => {
                    this.validCityValue = false;
                    this.isEssentialFormValidToBeSubmitted();
                });

                setTimeout(() => {
                    if (document.querySelector('#essential_lawyer_self_onboarding_contact_details_citySearch')) {
                        document.querySelector('#essential_lawyer_self_onboarding_contact_details_citySearch').setAttribute('autocomplete', 'nope');
                    }
                }, 1000);
            }

            if (document.querySelector('#freemium_lawyer_self_onboarding_contact_details_citySearch')) {
                this.autocomplete.addListener('place_changed', () => {
                    document.querySelector('#error-message-address').classList.add('d-none');
                    this.validCityValue = true;

                    this.isFreemiumPhoneValid();
                    this.isFreemiumFormValidToBeSubmitted();
                });

                document.querySelector('#freemium_lawyer_self_onboarding_contact_details_citySearch').addEventListener('blur', (event) => {
                    setTimeout(() => {
                        const activeElement = document.activeElement;

                        if (activeElement && activeElement.classList.contains('pac-item')) {
                            return;
                        }

                        if (!this.validCityValue) {
                            document.querySelector('#error-message-address').classList.remove('d-none');
                            this.validCityValue = false;
                            this.isFreemiumFormValidToBeSubmitted();
                        }
                    }, 200);
                });

                document.querySelector('#freemium_lawyer_self_onboarding_contact_details_citySearch').addEventListener('input', () => {
                    this.validCityValue = false;
                    this.isFreemiumFormValidToBeSubmitted();
                });

                setTimeout(() => {
                    if (document.querySelector('#freemium_lawyer_self_onboarding_contact_details_citySearch')) {
                        document.querySelector('#freemium_lawyer_self_onboarding_contact_details_citySearch').setAttribute('autocomplete', 'nope');
                    }
                }, 1000);
            }

            if (document.querySelector('#free_trial_lawyer_self_onboarding_contact_details_citySearch')) {
                this.autocomplete.addListener('place_changed', () => {
                    document.querySelector('#error-message-address').classList.add('d-none');
                    this.validCityValue = true;

                    this.isFreeTrialPhoneValid();
                    this.isFreeTrialFormValidToBeSubmitted();
                });

                document.querySelector('#free_trial_lawyer_self_onboarding_contact_details_citySearch').addEventListener('blur', (event) => {
                    setTimeout(() => {
                        const activeElement = document.activeElement;

                        if (activeElement && activeElement.classList.contains('pac-item')) {
                            return;
                        }

                        if (!this.validCityValue) {
                            document.querySelector('#error-message-address').classList.remove('d-none');
                            this.validCityValue = false;
                            this.isFreeTrialFormValidToBeSubmitted();
                        }
                    }, 200);
                });

                document.querySelector('#free_trial_lawyer_self_onboarding_contact_details_citySearch').addEventListener('input', () => {
                    this.validCityValue = false;
                    this.isFreeTrialFormValidToBeSubmitted();
                });

                setTimeout(() => {
                    if (document.querySelector('#free_trial_lawyer_self_onboarding_contact_details_citySearch')) {
                        document.querySelector('#free_trial_lawyer_self_onboarding_contact_details_citySearch').setAttribute('autocomplete', 'nope');
                    }
                }, 1000);
            }

            if (document.querySelector('#essential_lawyer_self_onboarding_contact_details_phone_phone')) {
                document.querySelector('#essential_lawyer_self_onboarding_contact_details_phone_phone').addEventListener('change', () => {
                    document.querySelector('#essential_lawyer_self_onboarding_contact_details_phone_phone').value
                        ? this.validPhoneValue = true
                        : this.validPhoneValue = false;

                    this.isEssentialFormValidToBeSubmitted();
                });
            }

            if (document.querySelector('#free_trial_lawyer_self_onboarding_contact_details_phone_phone')) {
                document.querySelector('#free_trial_lawyer_self_onboarding_contact_details_phone_phone').addEventListener('change', () => {
                    document.querySelector('#free_trial_lawyer_self_onboarding_contact_details_phone_phone').value
                        ? this.validPhoneValue = true
                        : this.validPhoneValue = false;

                    this.isFreeTrialFormValidToBeSubmitted();
                });
            }

            if (document.querySelector('#freemium_lawyer_self_onboarding_contact_details_phone')) {
                document.querySelector('#freemium_lawyer_self_onboarding_contact_details_phone_phone').addEventListener('change', () => {
                    document.querySelector('#freemium_lawyer_self_onboarding_contact_details_phone_phone').value
                        ? this.validPhoneValue = true
                        : this.validPhoneValue = false;

                    this.isFreemiumFormValidToBeSubmitted();
                });
            }
        }, 500);

        setTimeout(() => {
            if (document.querySelector('#essential_lawyer_self_onboarding_contact_details_citySearch')) {
                if (document.querySelector('#essential_lawyer_self_onboarding_contact_details_bar')?.value) {
                    this.isEssentialBarreauValid();
                }

                if (document.querySelector('#essential_lawyer_self_onboarding_contact_details_citySearch')?.value) {
                    this.validCityValue = true;
                }

                this.isEssentialPhoneValid();
                this.isEssentialFormValidToBeSubmitted();
            }

            if (document.querySelector('#free_trial_lawyer_self_onboarding_contact_details_citySearch')) {
                if (document.querySelector('#free_trial_lawyer_self_onboarding_contact_details_citySearch')?.value) {
                    this.isFreeTrialBarreauValid();
                }

                if (document.querySelector('#free_trial_lawyer_self_onboarding_contact_details_citySearch')?.value) {
                    this.validCityValue = true;
                }

                this.isFreeTrialPhoneValid();
                this.isFreeTrialFormValidToBeSubmitted();
            }

            if (document.querySelector('#freemium_lawyer_self_onboarding_contact_details_citySearch')) {
                if (document.querySelector('#freemium_lawyer_self_onboarding_contact_details_citySearch')?.value) {
                    this.isFreemiumBarreauValid();
                }

                if (document.querySelector('#freemium_lawyer_self_onboarding_contact_details_citySearch')?.value) {
                    this.validCityValue = true;
                }

                this.isFreemiumPhoneValid();
                this.isFreemiumFormValidToBeSubmitted();
            }
        }, 500);
    }

    isAddressAndSpecialismBarreauValid() {
        if (document.querySelector('#lawyer_address_and_specialism_barreau').value) {
            document.querySelector('#error-message-barreau').classList.add('d-none');
            this.validBarreauValue = true;
        } else {
            document.querySelector('#error-message-barreau').classList.remove('d-none');
            this.validBarreauValue = false;
        }
    }

    isEssentialBarreauValid() {
        if (document.querySelector('#essential_lawyer_self_onboarding_contact_details_bar').value) {
            document.querySelector('#error-message-barreau').classList.add('d-none');
            this.validBarreauValue = true;
        } else {
            document.querySelector('#error-message-barreau').classList.remove('d-none');
            this.validBarreauValue = false;
        }
    }

    isFreeTrialBarreauValid() {
        if (document.querySelector('#free_trial_lawyer_self_onboarding_contact_details_bar').value) {
            document.querySelector('#error-message-barreau').classList.add('d-none');
            this.validBarreauValue = true;
        } else {
            document.querySelector('#error-message-barreau').classList.remove('d-none');
            this.validBarreauValue = false;
        }
    }

    isFreemiumBarreauValid() {
        if (document.querySelector('#freemium_lawyer_self_onboarding_contact_details_bar').value) {
            document.querySelector('#error-message-barreau').classList.add('d-none');
            this.validBarreauValue = true;
        } else {
            document.querySelector('#error-message-barreau').classList.remove('d-none');
            this.validBarreauValue = false;
        }
    }

    isSpecialismValid() {
        let selectedValues = Array.from(document.querySelectorAll('.has-items'))[1];

        if (selectedValues) {
            this.validSpecialismValue = true;
        } else {
            this.validSpecialismValue = false;
        }
    }

    isEssentialPhoneValid() {
        if (document.querySelector('#essential_lawyer_self_onboarding_contact_details_phone_phone')) {
            document.querySelector('#essential_lawyer_self_onboarding_contact_details_phone_phone').value
                ? this.validPhoneValue = true
                : this.validPhoneValue = false;
        } else {
            this.validPhoneValue = false;
        }
    }

    isFreeTrialPhoneValid() {
        if (document.querySelector('#free_trial_lawyer_self_onboarding_contact_details_phone_phone')) {
            document.querySelector('#free_trial_lawyer_self_onboarding_contact_details_phone_phone').value
                ? this.validPhoneValue = true
                : this.validPhoneValue = false;
        } else {
            this.validPhoneValue = false;
        }
    }

    isFreemiumPhoneValid() {
        if (document.querySelector('#freemium_lawyer_self_onboarding_contact_details_phone_phone')) {
            document.querySelector('#freemium_lawyer_self_onboarding_contact_details_phone_phone').value
                ? this.validPhoneValue = true
                : this.validPhoneValue = false;
        } else {
            this.validPhoneValue = false;
        }
    }

    isAddressAndSpecialismFormValidToBeSubmitted() {
        if (this.validBarreauValue && this.validCityValue && this.validSpecialismValue) {
            document.querySelector('.citiesAutocompleteSubmitButton').disabled = false;
        } else {
            document.querySelector('.citiesAutocompleteSubmitButton').disabled = true;
        }
    }

    isEssentialFormValidToBeSubmitted() {
        if (this.validBarreauValue && this.validCityValue && this.validPhoneValue) {
            document.querySelector('.citiesAutocompleteSubmitButton').disabled = false;
        } else {
            document.querySelector('.citiesAutocompleteSubmitButton').disabled = true;
        }
    }

    isFreeTrialFormValidToBeSubmitted() {
        if (this.validBarreauValue && this.validCityValue && this.validPhoneValue) {
            document.querySelector('.citiesAutocompleteSubmitButton').disabled = false;
        } else {
            document.querySelector('.citiesAutocompleteSubmitButton').disabled = true;
        }
    }

    isFreemiumFormValidToBeSubmitted() {
        if (this.validBarreauValue && this.validCityValue && this.validPhoneValue) {
            document.querySelector('.citiesAutocompleteSubmitButton').disabled = false;
        } else {
            document.querySelector('.citiesAutocompleteSubmitButton').disabled = true;
        }
    }

    fillInAddress() {
        if (!this.autocomplete || (this.autocomplete && !this.autocomplete.getPlace())) {
            return;
        }

        const place = this.autocomplete.getPlace();

        this.cityIsValid = true;
        this.validCityValue = true;

        if (document.querySelector('#error-message-address')) {
            document.querySelector('#error-message-address').classList.add('d-none');
        }

        this.latitudeTarget.value = place.geometry.location.lat();
        this.longitudeTarget.value = place.geometry.location.lng();
        // add the city variable

        for (var i = 0; i < place.address_components.length; i++) {
            if (place.address_components[i] && place.address_components[i].types.includes('locality')) {
                this.cityTarget.value = place.address_components[i].long_name;
            } else if (place.address_components[i] && place.address_components[i].types.includes('postal_code')) {
                this.postalCodeTarget.value = place.address_components[i].long_name;
            }
        }

        if (
            document.querySelector('#barreau-container') && (
                '' !== this.cityTarget.value
                || document.querySelector('#barreau-container').classList.contains('d-none')
            )
        ) {
            this.fillBarreau(this.cityTarget.value);
        }

        const input = this.element.querySelectorAll('input');
        if (input.length > 0) {
            input.forEach((el) => {
                el.dispatchEvent(new Event('change', {bubbles: true}));
            });
        } else {
            console.log('no inputs founds');
        }
    }

    async fillBarreau(city) {
        if ('undefined' === typeof routes || !routes['app_city_getbarreauidbycity']) {
            return;
        }

        fetch(routes['app_city_getbarreauidbycity'], {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                'X-Requested-With': 'XMLHttpRequest',
            },
            body: new URLSearchParams({
                city: city,
            }),
        })
            .then((response) => response.json())
            .then((data) => {
                if (document.getElementById('lawyer_address_and_specialism_barreau')) {
                    document.getElementById('lawyer_address_and_specialism_barreau').tomselect.setValue(data.id, true);
                }

                if (document.getElementById('essential_lawyer_self_onboarding_contact_details_bar')) {
                    document.getElementById('essential_lawyer_self_onboarding_contact_details_bar').tomselect.setValue(data.id, true);
                }

                if (document.getElementById('free_trial_lawyer_self_onboarding_contact_details_bar')) {
                    document.getElementById('free_trial_lawyer_self_onboarding_contact_details_bar').tomselect.setValue(data.id, true);
                }

                if (document.getElementById('freemium_lawyer_self_onboarding_contact_details_bar')) {
                    document.getElementById('freemium_lawyer_self_onboarding_contact_details_bar').tomselect.setValue(data.id, true);
                }

                if (null !== document.querySelector('#barreau-container .ts-wrapper .item')) {
                    document.querySelector('#barreau-container .ts-wrapper .item').innerHTML = 'Barreau de ' + document.querySelector('#barreau-container .ts-wrapper .item').innerHTML;
                }

                document.querySelector('#barreau-container').classList.remove('d-none');

                // We triggered event change to update the validation if its triggered from before
                if (document.getElementById('lawyer_address_and_specialism_barreau')) {
                    document.querySelector('#lawyer_address_and_specialism_barreau').dispatchEvent(new Event('input'));
                    this.isAddressAndSpecialismBarreauValid();
                    this.isAddressAndSpecialismFormValidToBeSubmitted();
                }

                if (document.getElementById('essential_lawyer_self_onboarding_contact_details_bar')) {
                    document.querySelector('#essential_lawyer_self_onboarding_contact_details_bar').dispatchEvent(new Event('input'));
                    this.isEssentialBarreauValid();
                    this.isEssentialFormValidToBeSubmitted();
                }

                if (document.getElementById('free_trial_lawyer_self_onboarding_contact_details_bar')) {
                    document.querySelector('#free_trial_lawyer_self_onboarding_contact_details_bar').dispatchEvent(new Event('input'));
                    this.isFreeTrialBarreauValid();
                    this.isFreeTrialFormValidToBeSubmitted();
                }

                if (document.getElementById('freemium_lawyer_self_onboarding_contact_details_bar')) {
                    document.querySelector('#freemium_lawyer_self_onboarding_contact_details_bar').dispatchEvent(new Event('input'));
                    this.isFreemiumBarreauValid();
                    this.isFreemiumFormValidToBeSubmitted();
                }
            });
    }

    fillInBillingAddress() {
        if (!this.autocompleteBillingAddress || (this.autocompleteBillingAddress && !this.autocompleteBillingAddress.getPlace())) {
            return;
        }

        const place = this.autocompleteBillingAddress.getPlace();

        this.billingLatitudeTarget.value = place.geometry.location.lat();
        this.billingLongitudeTarget.value = place.geometry.location.lng();
        // add the city variable

        for (var i = 0; i < place.address_components.length; i++) {
            if (place.address_components[i].types[0] === 'locality') {
                this.billingCityTarget.value = place.address_components[i].long_name;
            } else if (place.address_components[i].types[0] === 'postal_code') {
                this.billingPostalCodeTarget.value = place.address_components[i].long_name;
            }
        }
    }
}
